import React, { useEffect, useMemo, useState } from "react";
import ProjectFieldFileControls, {
  ProjectSiteWalkFilesAppendControls,
} from "../ProjectFieldFileControls";
import { CommercialFieldFileIcon, fileUrlFromStoredValue } from "./CommercialFieldFileDisplay";
import { humanizeFieldKey } from "../ProjectPayloadView";
import {
  COMMERCIAL_DOMAIN_SCHEMAS,
  COMMERCIAL_DOMAINS,
  commercialFieldVisible,
  canonicalizeDepartmentList,
  multiselectValuesForOptions,
  normalizeMulti,
  normalizeSelectOptions,
  serviceSpeedSlug,
} from "./commercialProjectSchema";
import {
  subphaseSelectOptionsForPhase,
  applySubphaseEditToSummary,
  applySubphaseEditForPhaseKey,
  clearManualSubphaseOverrides,
  formatSubphaseLabel,
  isAccountingClosed,
  readSubphaseForCurrentPhase,
  readSubphaseForPhaseKey,
  SUBPHASE_KEYS,
} from "./commercialSubphases";
import { readCommercialDomains, repeaterToArray } from "./commercialProjectAcf";
import { readExpansionEditDomains } from "./expansionProjectForms";
import { EXPANSION_SUMMARY_DOMAIN } from "./expansionProjectSchema";
import { mergeCommercialFileFieldsFromServer, applyPermitPlanMaterialsToDraft } from "./commercialProjectFiles";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import {
  canAddNetworkingInstallations,
  canManageAllInspectorDailyProgressReports,
  canMarkNetworkingInstallationComplete,
  canEditExpansionSchemaField,
  isNetworkTechViewer,
  listMatrixUserEmailsByRoleName,
} from "./commercialRoleAccess";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import {
  EngineeringMaterialsCatalogEditor,
  MaterialCatalogSelect,
  findCatalogMaterial,
  unitLabelForMaterialName,
  useEngineeringMaterialsCatalog,
} from "./engineeringMaterialsCatalog";
import { overlayContractDerivedConstruction } from "./contractsDerivedFields";
import { formatContractCost } from "./contractsAccess";
import CdtInterconnectPanel from "./CdtInterconnectPanel";
import { expansionProjectHasCdt } from "./interconnectsUtils";
import { dailyProgressReportEditableByViewer } from "./inspectorReportCalendarUtils";
import PermitsSectionPanel, {
  ConstructionPermitsReadPanel,
  ExpansionPermitCoverageReadPanel,
} from "./PermitsSectionPanel";
import { PERMIT_EDIT_CONTEXT } from "./commercialPermitsSchema";
import { normalizeDateOnlyIso } from "./dateOnlyDisplay";
import { FiberFilesReadOnlySection } from "./CommercialSectionReadView";
import { viewerIsContractor } from "./contractorProjectAccess";

function FieldLabel({ field }) {
  return (
    <label className="commercial-field-label">
      {field.label}
      {field.hint ? (
        <span className="project-detail-muted" style={{ display: "block", fontWeight: 400, fontSize: 12 }}>
          {field.hint}
        </span>
      ) : null}
    </label>
  );
}

function MultiselectInput({ value, options, onChange, disabled }) {
  const selected = useMemo(
    () => new Set(multiselectValuesForOptions(value, options)),
    [value, options]
  );
  return (
    <div className="commercial-multiselect">
      {options.map((opt) => (
        <label key={opt} className="commercial-multiselect-option">
          <input
            type="checkbox"
            checked={selected.has(opt)}
            disabled={disabled}
            onChange={(e) => {
              const next = new Set(selected);
              if (e.target.checked) next.add(opt);
              else next.delete(opt);
              onChange([...next]);
            }}
          />
          {opt}
        </label>
      ))}
    </div>
  );
}

function UserRoleMultiselectField({ field, value, onChange, disabled }) {
  const options = useMemo(
    () => listMatrixUserEmailsByRoleName(field.roleNames || field.roleName || "", field.roleTypeIds),
    [field.roleName, field.roleNames, field.roleTypeIds]
  );
  const selected = useMemo(() => new Set(normalizeMulti(value)), [value]);
  return (
    <div className="commercial-field-row">
      <FieldLabel field={field} />
      <div className="commercial-field-value">
        {options.length === 0 ? (
          <span className="project-detail-muted" style={{ fontSize: 12 }}>
            No {field.roleName || "matching"} role users in User Matrix yet.
          </span>
        ) : (
          <div className="commercial-multiselect">
            {options.map((email) => (
              <label key={email} className="commercial-multiselect-option">
                <input
                  type="checkbox"
                  checked={selected.has(email)}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) next.add(email);
                    else next.delete(email);
                    onChange(
                      [...next].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
                    );
                  }}
                />
                {email}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function resolveCommercialFileFieldPath(field, pathPrefix) {
  const prefix = Array.isArray(pathPrefix) ? pathPrefix.filter((p) => p != null && p !== "") : [];
  // Cross-domain aliases (e.g. Engineering asbuilt → construction_details) store under valueDomain.
  if (field?.valueDomain && field.valueDomain !== prefix[0]) {
    return [field.valueDomain, field.key];
  }
  return [...prefix, field.key];
}

export function CommercialFieldInput({
  field,
  value,
  onChange,
  disabled,
  ctx,
  pathPrefix,
  fileEditing,
  materialsCatalog,
  materialItemName,
  subphaseOptionsForPhaseFn,
}) {
  if (!commercialFieldVisible(field, ctx)) return null;

  if (field.type === "readonly" || field.computed) {
    let display =
      value != null && String(value).trim() !== "" ? String(value) : "—";
    if (field.key === "contract_cost") {
      display = formatContractCost(value);
    }
    if (field.derivedFromContract && ctx.contractDerivedMeta?._contractNumber) {
      display = (
        <>
          {display}
          <span className="project-detail-muted" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
            From contract #{ctx.contractDerivedMeta._contractNumber}
            {ctx.contractDerivedMeta._contractTitle ? ` — ${ctx.contractDerivedMeta._contractTitle}` : ""}
          </span>
        </>
      );
    }
    return (
      <div className="commercial-field-row" key={field.key}>
        <FieldLabel field={field} />
        <div className="commercial-field-value">{display}</div>
      </div>
    );
  }

  if (field.type === "bool") {
    return (
      <div className="commercial-field-row" key={field.key}>
        <FieldLabel field={field} />
        <div className="commercial-field-value">
          <label className="commercial-bool-label">
            <input
              type="checkbox"
              checked={Boolean(value)}
              disabled={disabled}
              onChange={(e) => onChange(e.target.checked)}
              aria-label={field.label || field.key}
            />
          </label>
        </div>
      </div>
    );
  }

  if (field.type === "select") {
    if (field.optionsFromPhase && field.key === "subphase" && isAccountingClosed(ctx.summary, ctx.domains)) {
      return (
        <div className="commercial-field-row" key={field.key}>
          <FieldLabel field={field} />
          <div className="commercial-field-value">{formatSubphaseLabel("closed")}</div>
        </div>
      );
    }
    const permittingOverviewSubphase =
      field.optionsFromPhase &&
      field.key === "subphase" &&
      ctx.permittingOverviewEdit &&
      ctx.summaryDomainKey === COMMERCIAL_DOMAINS.SUMMARY;
    const phaseSubphaseOptions = permittingOverviewSubphase
      ? subphaseSelectOptionsForPhase("Permitting", ctx.summary)
      : field.optionsFromPhase && ctx.summary?.phase
        ? (subphaseOptionsForPhaseFn || subphaseSelectOptionsForPhase)(ctx.summary.phase, ctx.summary)
        : null;
    const options = phaseSubphaseOptions ?? normalizeSelectOptions(field.options);
    if (field.optionsFromPhase && options.length === 0) return null;
    const selectValue = field.optionsFromPhase
      ? permittingOverviewSubphase
        ? readSubphaseForPhaseKey(ctx.summary, SUBPHASE_KEYS.PERMITTING) ||
          (value != null ? String(value).trim() : "")
        : value != null && String(value).trim() !== ""
          ? String(value)
          : readSubphaseForCurrentPhase(ctx.summary)
      : field.key === "service_speed"
        ? serviceSpeedSlug(value)
        : value != null
          ? String(value)
          : "";
    return (
      <div className="commercial-field-row" key={field.key}>
        <FieldLabel
          field={
            permittingOverviewSubphase
              ? {
                  ...field,
                  label: "Permitting status",
                  hint: "Tracked independently of the project’s current phase.",
                }
              : field
          }
        />
        <div className="commercial-field-value">
          <select
            className="commercial-field-input"
            value={selectValue}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">—</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  if (field.type === "multiselect") {
    const options = field.options || [];
    return (
      <div className="commercial-field-row" key={field.key}>
        <FieldLabel field={field} />
        <div className="commercial-field-value">
          <MultiselectInput
            value={multiselectValuesForOptions(value, options)}
            options={options}
            disabled={disabled}
            onChange={onChange}
          />
        </div>
      </div>
    );
  }

  if (field.type === "material_catalog") {
    return (
      <div className="commercial-field-row" key={field.key}>
        <FieldLabel field={field} />
        <div className="commercial-field-value">
          <MaterialCatalogSelect
            className="commercial-field-input"
            value={value}
            onChange={
              disabled
                ? null
                : (slug, hit) => {
                    onChange(slug, hit);
                  }
            }
            catalog={materialsCatalog}
          />
        </div>
      </div>
    );
  }

  if (field.type === "user_role_multiselect") {
    return (
      <UserRoleMultiselectField
        key={field.key}
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (field.type === "user_role_select") {
    const rolePatterns = field.roleNames || field.roleName || "Splicer";
    const options = listMatrixUserEmailsByRoleName(rolePatterns, field.roleTypeIds);
    const current = value != null ? String(value).trim() : "";
    const optionsWithCurrent =
      current && !options.some((e) => e.toLowerCase() === current.toLowerCase())
        ? [current, ...options]
        : options;
    const roleLabel = Array.isArray(rolePatterns)
      ? rolePatterns.join(" / ")
      : rolePatterns || "Splicer";
    return (
      <div className="commercial-field-row" key={field.key}>
        <FieldLabel field={field} />
        <div className="commercial-field-value">
          <select
            className="commercial-field-input"
            value={current}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">—</option>
            {optionsWithCurrent.map((email) => (
              <option key={email} value={email}>
                {email}
              </option>
            ))}
          </select>
          {options.length === 0 ? (
            <span className="project-detail-muted" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
              No users with the {roleLabel} role in User Matrix.
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  if (field.type === "file") {
    const path = resolveCommercialFileFieldPath(field, pathPrefix);
    const hasFile = Boolean(fileUrlFromStoredValue(value));
    return (
      <div className="commercial-field-row" key={field.key}>
        <FieldLabel field={field} />
        <div className="commercial-field-value">
          {fileEditing && !disabled ? (
            <ProjectFieldFileControls
              projectId={fileEditing.projectId}
              fieldPath={path}
              value={value}
              getAuthHeaders={fileEditing.getAuthHeaders}
              onUpdated={(result) => fileEditing.onFileUpdated?.(result)}
              accept={field.accept}
            />
          ) : hasFile ? (
            <CommercialFieldFileIcon value={value} fieldKey={field.key} accept={field.accept} />
          ) : (
            <span className="project-detail-muted">—</span>
          )}
        </div>
      </div>
    );
  }

  if (field.type === "multifile") {
    const path = resolveCommercialFileFieldPath(field, pathPrefix);
    const rawRows = Array.isArray(value) ? value : value ? [value] : [];
    // Skip null/empty slots left by older remove behavior (usa_ticket/asbuilt).
    const rows = rawRows
      .map((row, idx) => ({ row, idx }))
      .filter(({ row }) => {
        if (row == null || row === "") return false;
        if (typeof row === "string") return Boolean(row.trim());
        if (typeof row === "object") {
          return Boolean(
            fileUrlFromStoredValue(row) ||
              (typeof row.filename === "string" && row.filename.trim())
          );
        }
        return false;
      });
    return (
      <div className="commercial-field-row" key={field.key}>
        <FieldLabel field={field} />
        <div className="commercial-field-value">
          {rows.length === 0 ? <span className="project-detail-muted">No files</span> : null}
          {rows.map(({ row, idx }) => (
            <div key={idx} style={{ marginBottom: 8 }}>
              {fileEditing && !disabled ? (
                <ProjectFieldFileControls
                  projectId={fileEditing.projectId}
                  fieldPath={[...path, idx, "site_walk_file"]}
                  value={
                    typeof row === "string"
                      ? row
                      : row && typeof row === "object"
                        ? row
                        : ""
                  }
                  getAuthHeaders={fileEditing.getAuthHeaders}
                  onUpdated={(result) => fileEditing.onFileUpdated?.(result)}
                  accept={field.accept}
                />
              ) : (
                <span>{typeof row === "string" ? row : JSON.stringify(row)}</span>
              )}
            </div>
          ))}
          {fileEditing && !disabled ? (
            <ProjectSiteWalkFilesAppendControls
              projectId={fileEditing.projectId}
              fieldPath={path}
              getAuthHeaders={fileEditing.getAuthHeaders}
              onUpdated={(result) => fileEditing.onFileUpdated?.(result)}
              accept={field.accept}
            />
          ) : null}
        </div>
      </div>
    );
  }

  if (field.type === "fiber_az_otdr") {
    const files =
      value && typeof value === "object" && !Array.isArray(value) ? value : { A: {}, Z: {} };
    return (
      <div className="commercial-field-row" key={field.key}>
        <FieldLabel field={field} />
        <div className="commercial-field-value">
          {["A", "Z"].map((loc) => (
            <div key={loc} style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{loc} location OTDR</div>
              {fileEditing ? (
                <ProjectFieldFileControls
                  projectId={fileEditing.projectId}
                  fieldPath={[...pathPrefix, field.key, loc, "otdr"]}
                  value={files[loc]?.otdr}
                  getAuthHeaders={fileEditing.getAuthHeaders}
                  onUpdated={(result) => fileEditing.onFileUpdated?.(result)}
                  accept=".zip"
                />
              ) : (
                <span>{files[loc]?.otdr ? String(files[loc].otdr) : "—"}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "repeater") {
    const rows = repeaterToArray(value);
    const viewerEmail = ctx?.viewerEmail || getViewerEmailFromStorage();
    const inspectorOwnReportsOnly =
      field.key === "daily_progress_reports" &&
      Boolean(ctx?.inspectorDailyProgressEdit) &&
      !canManageAllInspectorDailyProgressReports(viewerEmail);
    const canAddRows =
      field.key === "installations" ? canAddNetworkingInstallations(viewerEmail) : true;
    return (
      <div className="commercial-repeater" key={field.key}>
        <h4 className="commercial-repeater-title">{field.label}</h4>
        {rows.length === 0 ? <p className="project-detail-muted">No rows yet.</p> : null}
        {rows.map((row, rowIdx) => {
          const rowEditable = !disabled && (!inspectorOwnReportsOnly || dailyProgressReportEditableByViewer(row, viewerEmail));
          const networkTechInstallLock =
            field.key === "installations" && isNetworkTechViewer(viewerEmail);
          return (
          <div key={rowIdx} className="commercial-repeater-card">
            <div className="commercial-repeater-card-head">
              <strong>
                {field.rowLabel || "Row"} {rowIdx + 1}
              </strong>
              {rowEditable && canAddRows ? (
                <button
                  type="button"
                  className="email-triggers-button"
                  style={{ fontSize: 12, padding: "4px 8px" }}
                  onClick={() => {
                    const next = rows.filter((_, i) => i !== rowIdx);
                    onChange(next);
                  }}
                >
                  Remove
                </button>
              ) : null}
            </div>
            {(field.fields || []).map((sub) => {
              const subDisabled =
                field.key === "installations" && sub.key === "complete"
                  ? disabled ||
                    !canMarkNetworkingInstallationComplete(
                      getViewerEmailFromStorage(),
                      row?.technician
                    )
                  : networkTechInstallLock
                    ? true
                    : disabled || !rowEditable;
              const nestedMaterialItem =
                field.key === "materials" && sub.key === "quantity"
                  ? row?.slug || row?.item
                  : materialItemName;
              return (
              <CommercialFieldInput
                key={sub.key}
                field={sub}
                value={sub.type === "material_catalog" ? row?.slug || row?.[sub.key] : row?.[sub.key]}
                onChange={(v, hit) => {
                  const next = rows.map((r, i) => {
                    if (i !== rowIdx) return r;
                    if (sub.type === "material_catalog") {
                      const mat = hit || findCatalogMaterial(materialsCatalog, v);
                      return {
                        ...r,
                        slug: mat?.slug || String(v || "").trim(),
                        item: mat?.name || String(v || "").trim(),
                      };
                    }
                    return { ...r, [sub.key]: v };
                  });
                  onChange(next);
                }}
                disabled={subDisabled}
                ctx={ctx}
                pathPrefix={[...pathPrefix, field.key, rowIdx]}
                fileEditing={fileEditing}
                materialsCatalog={materialsCatalog}
                materialItemName={nestedMaterialItem}
              />
              );
            })}
          </div>
          );
        })}
        {!disabled && canAddRows ? (
          <button
            type="button"
            className="email-triggers-button email-triggers-button-primary"
            style={{ fontSize: 13 }}
            onClick={() =>
              onChange([
                ...rows,
                inspectorOwnReportsOnly
                  ? { reported_by: String(viewerEmail || "").trim() }
                  : {},
              ])
            }
          >
            Add {field.rowLabel || "row"}
          </button>
        ) : null}
      </div>
    );
  }

  const inputType =
    field.type === "date" ? "date" : field.type === "number" || field.type === "currency" ? "number" : "text";
  const Tag = field.type === "textarea" ? "textarea" : "input";
  const qtyUnit =
    field.key === "quantity" && materialItemName != null && String(materialItemName).trim() !== ""
      ? unitLabelForMaterialName(materialItemName)
      : null;
  // ACF legacy dates are often MM/DD/YYYY; <input type="date"> only accepts YYYY-MM-DD.
  const inputValue =
    field.type === "date"
      ? normalizeDateOnlyIso(value)
      : value != null
        ? String(value)
        : "";

  return (
    <div className="commercial-field-row" key={field.key}>
      <FieldLabel field={field} />
      <div className="commercial-field-value">
        {qtyUnit ? <span className="eng-materials-qty-unit">{qtyUnit}</span> : null}
        <Tag
          className={`commercial-field-input${field.type === "textarea" ? " commercial-field-textarea" : ""}`}
          type={Tag === "input" ? inputType : undefined}
          value={inputValue}
          disabled={disabled}
          rows={field.type === "textarea" ? 3 : undefined}
          step={field.type === "currency" ? "0.01" : undefined}
          min={field.type === "currency" ? "0" : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

/**
 * Commercial landing tab — controlled by parent when editing (save on Done editing).
 */
export default function CommercialProjectSectionPanel({
  domainKey,
  projectRow,
  domains,
  onDomainsChange,
  editing,
  projectId,
  getAuthHeaders,
  onUpdated,
  onFileSynced,
  schemas = COMMERCIAL_DOMAIN_SCHEMAS,
  summaryDomainKey = COMMERCIAL_DOMAINS.SUMMARY,
  subphaseOptionsForPhaseFn,
  fullAdminEdit = true,
  tabRoleEdit = false,
  permittingOverviewEdit = false,
  inspectorDailyProgressEdit = false,
  constructionAsbuiltEdit = false,
  networkingTabRoleEdit = false,
  showLinkedFiberFiles = false,
  contractDerivedConstruction,
  interconnectFields,
  onInterconnectFieldsChange,
  permitsEditContext = PERMIT_EDIT_CONTEXT.ENGINEERING,
  permitCoverage = null,
  apiUrl,
}) {
  const schema = schemas[domainKey];
  const [localDomains, setLocalDomains] = useState(() => readCommercialDomains(projectRow?.acf_groups_json));
  const controlled = domains != null && typeof onDomainsChange === "function";
  const activeDomains = controlled ? domains : localDomains;
  const setActiveDomains = controlled ? onDomainsChange : setLocalDomains;

  useEffect(() => {
    if (controlled) return;
    setLocalDomains(readCommercialDomains(projectRow?.acf_groups_json));
  }, [controlled, projectRow?.acf_groups_json, projectRow?.id]);

  const viewerEmail = getViewerEmailFromStorage();
  const materialsCatalog = useEngineeringMaterialsCatalog();

  const ctx = useMemo(
    () => ({
      summary: activeDomains[summaryDomainKey] || {},
      networking: activeDomains[COMMERCIAL_DOMAINS.NETWORKING] || {},
      domains: activeDomains,
      viewerEmail,
      summaryDomainKey,
      permittingOverviewEdit:
        permittingOverviewEdit &&
        domainKey === summaryDomainKey &&
        summaryDomainKey === COMMERCIAL_DOMAINS.SUMMARY,
      inspectorDailyProgressEdit,
      contractDerivedMeta: contractDerivedConstruction?.fields || null,
    }),
    [
      activeDomains,
      summaryDomainKey,
      viewerEmail,
      permittingOverviewEdit,
      inspectorDailyProgressEdit,
      domainKey,
      contractDerivedConstruction,
    ]
  );

  const displayDomains = useMemo(() => {
    if (!contractDerivedConstruction?.fields) return activeDomains;
    return overlayContractDerivedConstruction(activeDomains, contractDerivedConstruction.fields);
  }, [activeDomains, contractDerivedConstruction]);

  const sectionVisible = schema?.sectionVisible ? schema.sectionVisible(ctx) : true;

  const handleFileUpdated = (uploadResult) => {
    if (uploadResult?.project?.acf_groups_json) {
      const server =
        summaryDomainKey === EXPANSION_SUMMARY_DOMAIN
          ? readExpansionEditDomains(uploadResult.project, uploadResult.project.acf_domains)
          : readCommercialDomains(uploadResult.project.acf_groups_json);
      setActiveDomains((prev) => {
        let next = mergeCommercialFileFieldsFromServer(prev || {}, server);
        const ppm = uploadResult?.permitPlanMaterials;
        if (
          isPluginEnabled(PLUGIN_IDS.PERMIT_PLAN_MATERIAL_EXTRACTION) &&
          ppm?.applied &&
          Array.isArray(ppm.materials) &&
          domainKey === COMMERCIAL_DOMAINS.ENGINEERING
        ) {
          next = applyPermitPlanMaterialsToDraft(next, domainKey, ppm.permitIndex, ppm.materials);
        }
        return next;
      });
    } else if (Array.isArray(uploadResult?.fieldPath) && uploadResult.fieldPath.length === 2) {
      const [dk, fk] = uploadResult.fieldPath;
      setActiveDomains((prev) => ({
        ...prev,
        [dk]: {
          ...(prev?.[dk] || {}),
          [fk]: uploadResult.url ?? null,
        },
      }));
    }
    if (onFileSynced) onFileSynced(uploadResult);
    else onUpdated?.();
  };

  const fileEditing = editing
    ? {
        projectId,
        getAuthHeaders,
        onFileUpdated: handleFileUpdated,
      }
    : null;

  const setField = (key, value, storageDomainKey = domainKey) => {
    const fieldDef = (schema.fields || []).find((f) => f.key === key);
    if (fieldDef?.derivedFromContract) return;
    if (key === "subphase" && domainKey === summaryDomainKey) {
      const permittingOverview =
        permittingOverviewEdit &&
        summaryDomainKey === COMMERCIAL_DOMAINS.SUMMARY &&
        domainKey === summaryDomainKey;
      setActiveDomains((prev) => ({
        ...prev,
        [domainKey]: permittingOverview
          ? applySubphaseEditForPhaseKey(prev[domainKey] || {}, SUBPHASE_KEYS.PERMITTING, value)
          : applySubphaseEditToSummary(prev[domainKey] || {}, value),
      }));
      return;
    }
    if (key === "phase" && storageDomainKey === summaryDomainKey) {
      setActiveDomains((prev) => {
        const slice = { ...(prev[storageDomainKey] || {}), [key]: value };
        // Phase change invalidates sticky subphase overrides for the prior phase.
        return {
          ...prev,
          [storageDomainKey]: clearManualSubphaseOverrides(slice),
        };
      });
      return;
    }
    setActiveDomains((prev) => ({
      ...prev,
      [storageDomainKey]: { ...(prev[storageDomainKey] || {}), [key]: value },
    }));
  };

  if (!schema) {
    return <p className="project-detail-muted">Unknown section.</p>;
  }

  if (!editing) {
    return null;
  }

  if (!sectionVisible) {
    return (
      <p className="project-detail-muted">
        This section applies when{" "}
        <strong>{domainKey === COMMERCIAL_DOMAINS.FIBER ? "Fiber" : humanizeFieldKey(domainKey)}</strong> is
        selected in Project summary → Departments.
      </p>
    );
  }

  const payload = displayDomains[domainKey] || {};
  /** Scoped roles (OSP, 811, inspector, field-level editRoles, etc.): only show editable fields. */
  const hideNonEditableFields = !fullAdminEdit;
  const canEditEngineeringExtras = fullAdminEdit || tabRoleEdit;
  const contractorViewer = viewerIsContractor(viewerEmail);
  if (contractorViewer && domainKey === COMMERCIAL_DOMAINS.CONSTRUCTION) {
    return (
      <section className="commercial-section-panel">
        <ConstructionPermitsReadPanel
          permits={displayDomains?.[COMMERCIAL_DOMAINS.ENGINEERING]?.permits}
        />
      </section>
    );
  }
  const showPermitCoverage =
    !contractorViewer &&
    summaryDomainKey === EXPANSION_SUMMARY_DOMAIN &&
    (domainKey === COMMERCIAL_DOMAINS.ENGINEERING || domainKey === COMMERCIAL_DOMAINS.CONSTRUCTION);

  return (
    <section className="commercial-section-panel">
      <h3 className="commercial-section-title">{schema.label}</h3>
      {showPermitCoverage ? (
        <ExpansionPermitCoverageReadPanel
          projectId={projectId}
          permitCoverage={permitCoverage}
          confirmationValue={displayDomains?.[COMMERCIAL_DOMAINS.ENGINEERING]?.permit_coverage_confirmation}
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
        />
      ) : null}
      {domainKey === COMMERCIAL_DOMAINS.ENGINEERING ? (
        <EngineeringMaterialsCatalogEditor viewerEmail={viewerEmail} />
      ) : null}
      {domainKey === COMMERCIAL_DOMAINS.ENGINEERING &&
      summaryDomainKey === EXPANSION_SUMMARY_DOMAIN &&
      expansionProjectHasCdt(projectRow) &&
      canEditEngineeringExtras ? (
        <CdtInterconnectPanel
          projectRow={projectRow}
          projectId={projectId}
          interconnectFields={interconnectFields}
          onInterconnectFieldsChange={onInterconnectFieldsChange}
          editing={editing}
          canEdit={canEditEngineeringExtras}
          apiUrl={apiUrl || null}
          getAuthHeaders={getAuthHeaders}
        />
      ) : null}
      <div className="commercial-fields">
        {(schema.fields || []).map((field) => {
          if (!commercialFieldVisible(field, ctx)) return null;
          const canEditField = canEditExpansionSchemaField(field, viewerEmail, {
            fullAdminEdit,
            tabRoleEdit,
            inspectorDailyProgressEdit,
            constructionAsbuiltEdit,
            networkingTabRoleEdit,
          });
          if (hideNonEditableFields && !canEditField) return null;
          const storageDomainKey = field.valueDomain || domainKey;
          const storagePayload =
            storageDomainKey === domainKey ? payload : displayDomains[storageDomainKey] || {};
          let fieldValue = storagePayload[field.key];
          if (field.key === "departments") {
            fieldValue = canonicalizeDepartmentList(
              payload.departments ?? payload.department_involvement
            );
          }
          if (field.key === "subphase" && domainKey === summaryDomainKey) {
            fieldValue = ctx.permittingOverviewEdit
              ? readSubphaseForPhaseKey(payload, SUBPHASE_KEYS.PERMITTING) || readSubphaseForCurrentPhase(payload)
              : readSubphaseForCurrentPhase(payload);
          }
          if (field.key === "permits") {
            return (
              <PermitsSectionPanel
                key={field.key}
                title={field.label}
                permits={fieldValue}
                onChange={(v) => setField(field.key, v, storageDomainKey)}
                editing={canEditField}
                editContext={permitsEditContext}
                ctx={ctx}
                pathPrefix={[domainKey, field.key]}
                fileEditing={canEditField ? fileEditing : null}
                materialsCatalog={materialsCatalog}
              />
            );
          }
          return (
          <CommercialFieldInput
            key={field.key}
            field={field}
            value={fieldValue}
            onChange={(v) => setField(field.key, v, storageDomainKey)}
            disabled={!canEditField}
            ctx={ctx}
            pathPrefix={[domainKey]}
            fileEditing={fileEditing}
            materialsCatalog={materialsCatalog}
            subphaseOptionsForPhaseFn={subphaseOptionsForPhaseFn}
          />
          );
        })}
        {showLinkedFiberFiles && domainKey === COMMERCIAL_DOMAINS.NETWORKING ? (
          <FiberFilesReadOnlySection
            domains={displayDomains}
            schemas={schemas}
            summaryDomainKey={summaryDomainKey}
          />
        ) : null}
      </div>
    </section>
  );
}
